import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wrench, Plus, Camera, Home, MapPin, Trash2, Edit, X, ArrowDown, ArrowUp,
  History as HistoryIcon, AlertCircle, Image as ImageIcon, ChevronDown, ChevronRight,
} from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';
import { compressImage } from '../utils/imageCompression';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const STALE_DAYS = 14; // days a tool can be "out" before we show a red badge

// Preset categories — employee can also type a custom one
const PRESET_CATEGORIES = [
  'Power Tools',
  'Cordless Tools',
  'Hand Tools',
  'Measuring',
  'Safety',
  'Ladders & Access',
  'Plumbing',
  'Electrical',
  'Painting',
  'Cleaning',
  'Other',
];

const UNCATEGORIZED = 'Uncategorized';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(`${iso.length <= 10 ? iso + 'T00:00:00' : iso}`)
    .toLocaleDateString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', year: 'numeric' });
}

function resolvePhotoUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
}

export default function MyPersonalTools({ user }) {
  const [tools, setTools] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null); // tool object or { _new: true }
  const [moveTarget, setMoveTarget] = useState(null);   // tool we're adding a move for
  const [historyTool, setHistoryTool] = useState(null); // tool whose history is open
  const [filterProject, setFilterProject] = useState(''); // filter UI
  const [collapsed, setCollapsed] = useState({}); // { categoryName: true/false }

  const fetchTools = async () => {
    try {
      const { data } = await axios.get(`${API}/personal-tools`);
      setTools(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load your tools');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchTools(); fetchProjects(); }, []);

  const onSiteCounts = useMemo(() => {
    const m = {};
    tools.forEach((t) => {
      if (t.status?.status === 'out' && t.status?.project_id) {
        m[t.status.project_id] = (m[t.status.project_id] || 0) + 1;
      }
    });
    return m;
  }, [tools]);

  const filtered = useMemo(() => {
    if (!filterProject) return tools;
    if (filterProject === '__home__') return tools.filter((t) => t.status?.status !== 'out');
    return tools.filter((t) => t.status?.project_id === filterProject);
  }, [tools, filterProject]);

  // Distinct list of categories actually in use (preserves preset order, then any custom names alphabetically)
  const allCategories = useMemo(() => {
    const used = new Set();
    tools.forEach((t) => used.add((t.category || '').trim() || UNCATEGORIZED));
    const ordered = [];
    PRESET_CATEGORIES.forEach((c) => { if (used.has(c)) { ordered.push(c); used.delete(c); } });
    const customs = [...used].filter((c) => c !== UNCATEGORIZED).sort();
    if (used.has(UNCATEGORIZED)) { used.delete(UNCATEGORIZED); }
    const tail = customs;
    if (tools.some((t) => !(t.category || '').trim())) tail.push(UNCATEGORIZED);
    return [...ordered, ...tail];
  }, [tools]);

  // Group the (filtered) tools by category
  const grouped = useMemo(() => {
    const byCat = {};
    filtered.forEach((t) => {
      const cat = (t.category || '').trim() || UNCATEGORIZED;
      if (!byCat[cat]) byCat[cat] = [];
      byCat[cat].push(t);
    });
    // Return categories in the same order as allCategories (skip empty ones)
    return allCategories.map((cat) => ({ cat, items: byCat[cat] || [] })).filter((g) => g.items.length > 0);
  }, [filtered, allCategories]);

  const toggleCategory = (cat) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  const handleBringAllBack = async (projectId) => {
    const proj = projects.find((p) => p.id === projectId);
    const name = proj?.name || 'this site';
    if (!window.confirm(`Mark every tool currently on "${name}" as brought back home?`)) return;
    try {
      const { data } = await axios.post(`${API}/personal-tools/bring-all-back`, { project_id: projectId });
      toast.success(`Brought back ${data.brought_back || 0} tool(s)`);
      await fetchTools();
    } catch {
      toast.error('Failed to update tools');
    }
  };

  const handleDeleteTool = async (tool) => {
    if (!window.confirm(`Delete "${tool.name}" and its entire move history?`)) return;
    try {
      await axios.delete(`${API}/personal-tools/${tool.id}`);
      setTools((prev) => prev.filter((t) => t.id !== tool.id));
      toast.success('Tool removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  // List of projects that currently have at least one of my tools on site
  const activeSiteProjects = projects.filter((p) => onSiteCounts[p.id] > 0);

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Wrench className="w-6 h-6 text-amber-600" />
            <h1 className="text-xl font-bold text-gray-900">My Personal Tool Log</h1>
          </div>
          <button
            onClick={() => setEditingTool({ _new: true, name: '', photo_url: '', notes: '' })}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold shadow"
            data-testid="add-tool-btn"
          >
            <Plus className="w-4 h-4" />
            Add Tool
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Track your own tools, where you took them, and when you brought them back.</p>

        {/* Filter chips */}
        {tools.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3" data-testid="filter-chips">
            <button
              onClick={() => setFilterProject('')}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${filterProject === '' ? 'bg-amber-600 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
              data-testid="filter-all"
            >
              All ({tools.length})
            </button>
            <button
              onClick={() => setFilterProject('__home__')}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${filterProject === '__home__' ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
              data-testid="filter-home"
            >
              <Home className="w-3 h-3" />
              At home ({tools.length - Object.values(onSiteCounts).reduce((a, b) => a + b, 0)})
            </button>
            {activeSiteProjects.map((p) => (
              <button
                key={p.id}
                onClick={() => setFilterProject(p.id)}
                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${filterProject === p.id ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
                data-testid={`filter-project-${p.id}`}
              >
                <MapPin className="w-3 h-3" />
                {p.name} ({onSiteCounts[p.id]})
              </button>
            ))}
          </div>
        )}

        {/* "Bring all back" call-to-action when filtered to a project */}
        {filterProject && filterProject !== '__home__' && onSiteCounts[filterProject] > 0 && (
          <button
            onClick={() => handleBringAllBack(filterProject)}
            className="w-full mb-3 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center justify-center gap-2 shadow"
            data-testid="bring-all-back-btn"
          >
            <ArrowDown className="w-4 h-4" />
            Bring all {onSiteCounts[filterProject]} back from this site
          </button>
        )}

        {loading ? (
          <div className="text-center text-sm text-gray-500 py-10">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {tools.length === 0 ? "You haven't added any tools yet. Tap 'Add Tool' to start." : "No tools match this filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="tools-list">
            {grouped.map(({ cat, items }) => {
              const open = !collapsed[cat];
              return (
                <div key={cat} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`category-${cat}`}>
                  <button
                    onClick={() => toggleCategory(cat)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 border-b border-gray-100"
                    data-testid={`category-toggle-${cat}`}
                  >
                    <div className="flex items-center gap-2">
                      {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                      <span className="font-bold text-gray-900 text-sm">{cat}</span>
                      <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{items.length}</span>
                    </div>
                  </button>
                  {open && (
                    <div className="p-3 space-y-3">
                      {items.map((t) => (
                        <ToolCard
                          key={t.id}
                          tool={t}
                          onEdit={() => setEditingTool(t)}
                          onMove={() => setMoveTarget(t)}
                          onHistory={() => setHistoryTool(t)}
                          onDelete={() => handleDeleteTool(t)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editingTool && (
        <ToolFormDialog
          tool={editingTool}
          existingCustomCategories={[...new Set(
            tools.map((t) => (t.category || '').trim()).filter((c) => c && !PRESET_CATEGORIES.includes(c))
          )].sort()}
          onClose={() => setEditingTool(null)}
          onSaved={async () => { setEditingTool(null); await fetchTools(); }}
        />
      )}

      {moveTarget && (
        <MoveDialog
          tool={moveTarget}
          projects={projects}
          onClose={() => setMoveTarget(null)}
          onSaved={async () => { setMoveTarget(null); await fetchTools(); }}
        />
      )}

      {historyTool && (
        <HistoryDialog
          tool={historyTool}
          onClose={() => setHistoryTool(null)}
          onChanged={fetchTools}
        />
      )}
    </div>
  );
}

function ToolCard({ tool, onEdit, onMove, onHistory, onDelete }) {
  const status = tool.status || { status: 'home' };
  const isOut = status.status === 'out';
  const stale = isOut && (status.days_out || 0) >= STALE_DAYS;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex gap-3" data-testid={`tool-${tool.id}`}>
      <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
        {tool.photo_url ? (
          <img src={resolvePhotoUrl(tool.photo_url)} alt={tool.name} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-8 h-8 text-gray-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate">{tool.name}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {tool.category && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                  {tool.category}
                </span>
              )}
              {isOut ? (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${stale ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-blue-100 text-blue-800 border border-blue-200'}`}>
                  <MapPin className="w-3 h-3" />
                  {status.project_name || 'On site'}
                  {status.days_out > 0 && ` · ${status.days_out}d`}
                  {stale && <AlertCircle className="w-3 h-3" />}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <Home className="w-3 h-3" />
                  At home
                </span>
              )}
              {status.since && (
                <span className="text-[10px] text-gray-500">since {fmtDate(status.since)}</span>
              )}
            </div>
            {tool.notes && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{tool.notes}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1 mt-2">
          <button onClick={onMove} className="py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center gap-0.5" data-testid={`move-${tool.id}`}>
            <ArrowUp className="w-3 h-3" /> Move
          </button>
          <button onClick={onHistory} className="py-1.5 rounded-lg bg-gray-700 hover:bg-gray-800 text-white text-[11px] font-bold flex items-center justify-center gap-0.5" data-testid={`history-${tool.id}`}>
            <HistoryIcon className="w-3 h-3" /> History
          </button>
          <button onClick={onEdit} className="py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center gap-0.5" data-testid={`edit-${tool.id}`}>
            <Edit className="w-3 h-3" /> Edit
          </button>
          <button onClick={onDelete} className="py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-[11px] font-bold flex items-center justify-center gap-0.5" data-testid={`delete-${tool.id}`}>
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ToolFormDialog({ tool, existingCustomCategories = [], onClose, onSaved }) {
  const isNew = !!tool._new;
  const [name, setName] = useState(tool.name || '');
  const [notes, setNotes] = useState(tool.notes || '');
  const [photoUrl, setPhotoUrl] = useState(tool.photo_url || '');
  const initialCat = (tool.category || '').trim();
  const [category, setCategory] = useState(initialCat);
  const [addingCustom, setAddingCustom] = useState(initialCat && !PRESET_CATEGORIES.includes(initialCat) && !existingCustomCategories.includes(initialCat));
  const [customCategory, setCustomCategory] = useState(addingCustom ? initialCat : '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file, 1024, 1024, 0.85);
      const fd = new FormData();
      fd.append('file', compressed, file.name);
      const { data } = await axios.post(`${API}/personal-tools/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoUrl(data.photo_url);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Tool name is required'); return; }
    const finalCat = addingCustom ? customCategory.trim() : category.trim();
    setSaving(true);
    try {
      const body = { name: name.trim(), photo_url: photoUrl, notes: notes.trim(), category: finalCat };
      if (isNew) {
        await axios.post(`${API}/personal-tools`, body);
        toast.success('Tool added');
      } else {
        await axios.put(`${API}/personal-tools/${tool.id}`, body);
        toast.success('Tool updated');
      }
      onSaved();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="tool-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">{isNew ? 'Add Tool' : 'Edit Tool'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Tool name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. DeWalt drill"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            data-testid="tool-name-input"
            autoFocus
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Category</label>
          {!addingCustom ? (
            <div className="mt-1 flex gap-2">
              <select
                value={category}
                onChange={(e) => {
                  if (e.target.value === '__add_new__') {
                    setAddingCustom(true);
                    setCustomCategory('');
                  } else {
                    setCategory(e.target.value);
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="category-select"
              >
                <option value="">— No category —</option>
                {PRESET_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                {existingCustomCategories.length > 0 && (
                  <optgroup label="Your categories">
                    {existingCustomCategories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </optgroup>
                )}
                <option value="__add_new__">+ Add new category…</option>
              </select>
            </div>
          ) : (
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="New category name"
                className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="custom-category-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => { setAddingCustom(false); setCustomCategory(''); }}
                className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Photo</label>
          <div className="mt-1 flex items-center gap-3">
            <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
              {photoUrl ? (
                <img src={resolvePhotoUrl(photoUrl)} alt="Tool" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-8 h-8 text-gray-300" />
              )}
            </div>
            <div className="flex-1 space-y-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="upload-photo-btn"
              >
                <Camera className="w-4 h-4" />
                {uploading ? 'Uploading…' : (photoUrl ? 'Replace photo' : 'Add photo')}
              </button>
              {photoUrl && (
                <button
                  onClick={() => setPhotoUrl('')}
                  className="w-full text-xs text-red-600 hover:underline"
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Serial #, model, where you usually keep it…"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
            data-testid="tool-notes-input"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold disabled:bg-gray-200 disabled:text-gray-400" data-testid="save-tool-btn">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveDialog({ tool, projects, onClose, onSaved }) {
  // Default to opposite of current status: if at home → "out"; if out → "back"
  const initialDirection = tool.status?.status === 'out' ? 'back' : 'out';
  const [direction, setDirection] = useState(initialDirection);
  const [projectId, setProjectId] = useState(initialDirection === 'back' ? (tool.status?.project_id || '') : '');
  const [movedAt, setMovedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (direction === 'out' && !projectId) {
      toast.error('Pick the project/site you took it to');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/personal-tools/${tool.id}/moves`, {
        project_id: projectId,
        direction,
        moved_at: movedAt,
        notes,
      });
      toast.success('Move logged');
      onSaved();
    } catch {
      toast.error('Failed to log move');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="move-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Log move — {tool.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">What happened?</label>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <button
              onClick={() => setDirection('out')}
              className={`px-3 py-2 rounded-lg text-sm font-bold border flex items-center justify-center gap-1 ${direction === 'out' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
              data-testid="direction-out"
            >
              <ArrowUp className="w-4 h-4" /> Took to site
            </button>
            <button
              onClick={() => setDirection('back')}
              className={`px-3 py-2 rounded-lg text-sm font-bold border flex items-center justify-center gap-1 ${direction === 'back' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200'}`}
              data-testid="direction-back"
            >
              <ArrowDown className="w-4 h-4" /> Brought back
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Project / site {direction === 'out' ? '*' : '(optional)'}</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="move-project-select"
          >
            <option value="">— No specific project —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Date</label>
          <input
            type="date"
            value={movedAt}
            onChange={(e) => setMovedAt(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="move-date-input"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="move-notes-input"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:bg-gray-200 disabled:text-gray-400" data-testid="save-move-btn">
            {saving ? 'Saving…' : 'Save move'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryDialog({ tool, onClose, onChanged }) {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/personal-tools/${tool.id}/moves`);
      setMoves(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tool?.id]);

  const removeMove = async (id) => {
    if (!window.confirm('Delete this move entry?')) return;
    try {
      await axios.delete(`${API}/personal-tools/moves/${id}`);
      setMoves((prev) => prev.filter((m) => m.id !== id));
      onChanged && onChanged();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="history-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b bg-gradient-to-r from-gray-800 to-gray-700 text-white flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide opacity-80">Move history</p>
            <h3 className="font-bold truncate">{tool.name}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading ? (
            <p className="text-center text-sm text-gray-500 py-8">Loading…</p>
          ) : moves.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8">No moves logged yet.</p>
          ) : (
            moves.map((m) => {
              const out = m.direction === 'out';
              return (
                <div key={m.id} className={`rounded-lg border p-3 flex items-start gap-3 ${out ? 'bg-blue-50 border-blue-100' : 'bg-emerald-50 border-emerald-100'}`} data-testid={`move-${m.id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${out ? 'bg-blue-500' : 'bg-emerald-500'} text-white`}>
                    {out ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">
                      {out ? 'Took to' : 'Brought back from'} {m.project_name || '— no project —'}
                    </p>
                    <p className="text-xs text-gray-500">{fmtDate(m.moved_at)}</p>
                    {m.notes && <p className="text-xs text-gray-700 mt-1">{m.notes}</p>}
                  </div>
                  <button onClick={() => removeMove(m.id)} className="p-1 text-gray-400 hover:text-red-600 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
